import React, { useState, useEffect } from "react"
import Combobox                       from "@shared/combobox"
import Input                          from '@shared/input'
import { FontAwesomeIcon }            from "@fortawesome/react-fontawesome"
import { getRequest, pathname }       from '@react/helpers/fetch'

export const Product = ({
  product,
  technician,
  mbcContractId,
  mbcAllowOffBpu,
  usedBpuIds = [],
  onChange,
  updateProduct,
  updateBpuLine,
  removeProduct,
  index
}) => {
  const productObject  = { id: product.productId, name: product.name }
  const productOptions = product.productId ? [productObject] : []
  const bpuObject      = product.mbcLineId ? { id: product.mbcLineId, name: product.name } : null
  const bpuOptions     = bpuObject ? [bpuObject] : []

  const [stock, setStock]       = useState(null)
  const [bpuLines, setBpuLines] = useState([])

  // Lines added via "Ajouter un article BPU" / "Ajouter un article hors BPU"
  // carry an explicit _kind. Existing/legacy lines fall back to inference.
  const lineKind = product._kind ||
                   (product.mbcLineId ? "bpu" :
                    product.productId ? "off_bpu" : null)

  // Fetch BPU catalogue once we know the marché. Re-fetch when it changes.
  useEffect(() => {
    if (!mbcContractId) {
      setBpuLines([])
      return
    }
    getRequest(`/mbc_contracts/${mbcContractId}/mbc_lines.json`)
      .then(lines => setBpuLines(lines || []))
      .catch(err => {
        console.error("Failed to load BPU catalogue", err)
        setBpuLines([])
      })
  }, [mbcContractId])

  const onProductChange = newProduct => {
    if (!newProduct) return
    updateProduct(index, newProduct.id, newProduct.name)

    const technicianId = technician?.id || ""
    const params       = { product: newProduct.id, technician: technicianId }
    const path         = "/workorders/products/fetch_stocks"
    const format       = "json"
    const url          = pathname({ path, params, format })
    getRequest(url).then(res => setStock(res.html))
  }

  const onBpuChange = bpu => {
    updateBpuLine(index, bpu)
    setStock(null)
  }

  const onQuantityChange = quantity => onChange(index, quantity)

  const renderPicker = () => {
    // Off-marché flow: plain product autocomplete (the legacy behaviour)
    if (!mbcContractId) {
      return productObject.id
        ? renderSelectedProductLink()
        : <Combobox
            feed            = "/products/autocomplete.json"
            options         = { productOptions }
            value           = { product.productId || "" }
            prompt          = { I18n.t("products.new") }
            onSelectElement = { onProductChange }
            className       = "select-view"
            required
          />
    }

    // Marché flow. Selected line → readonly view.
    if (product.mbcLineId) return renderSelectedBpuPill()
    if (product.productId) return renderSelectedProductLink()

    // Empty marché row: kind dictates picker.
    if (lineKind === "off_bpu") {
      return (
        <Combobox
          feed            = "/products/autocomplete.json"
          options         = { productOptions }
          value           = { product.productId || "" }
          prompt          = { I18n.t("products.new") }
          onSelectElement = { onProductChange }
          className       = "select-view"
          required
        />
      )
    }

    const availableBpuLines = bpuLines.filter(l => !usedBpuIds.includes(l.id))
    const nameFor = l => `${l.favorite ? "★ " : ""}${l.code} — ${l.label}`

    return (
      <Combobox
        options         = { availableBpuLines.map(l => ({ id: l.id, name: nameFor(l), ...l })) }
        value           = { product.mbcLineId || "" }
        prompt          = { I18n.t("mbc_contracts.workorders.bpu_picker") }
        onSelectElement = { onBpuChange }
        className       = "select-view"
        required
      />
    )
  }

  const renderSelectedProductLink = () => (
    <a
      data-toggle = "modal"
      data-target = "#remote-modal"
      data-action = "click->modal#show"
      data-route  = { `/products/${productObject.id}?is_modal=true` }
    >
      { productObject.name }
    </a>
  )

  const renderSelectedBpuPill = () => (
    <span style={{ display: 'block', minWidth: 0, lineHeight: 1.4 }}>
      <span
        className="badge badge-info mr-2"
        style={{
          padding:      '2px 6px',
          fontSize:     '0.7rem',
          display:      'inline-block',
          width:        'auto',
          minWidth:     0,
          verticalAlign: 'middle'
        }}
      >
        BPU
      </span>
      <span style={{ wordBreak: 'break-word', verticalAlign: 'middle' }}>
        { product.name }
      </span>
    </span>
  )

  return (
    <>
      <div className="cocoon-grid">
        <div className="form-group mr-2 d-flex a-i-center">
          { renderPicker() }
        </div>
        <Input
          name         = "quantity"
          type         = "number"
          step         = "0.01"
          defaultValue = { product.quantity }
          className    = "form-control text-center"
          onChange     = { value => onQuantityChange(value) }
          required
        />
        <div className="mt-2 ml-2 text-center">
          <a className="mb-2 text-pointer">
            <FontAwesomeIcon icon="minus-circle" onClick={() => removeProduct(index)}/>
          </a>
        </div>
      </div>
      { stock && <div class="stock-row" dangerouslySetInnerHTML={{__html: stock}}></div> }
    </>
  )
}
